import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ShieldCheck, Award, Flame, Droplet, Wind, Hammer } from 'lucide-react';
import { fetchBySlug, fetchCollection } from '../lib/api';
import GalleryLightbox from '../components/GalleryLightbox';

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    async function loadData() {
      const data = await fetchBySlug('da', 'index');
      setPageData(data);

      const cols = await fetchCollection('galleri');
      setGalleries(cols || []);
    }
    loadData();
  }, []);

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (!pageData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-900">
        <div className="animate-pulse text-sm font-semibold tracking-widest uppercase">Henter indhold...</div>
      </div>
    );
  }

  // Flatten all images from galleries for a references preview
  const allReferenceImages = galleries.flatMap(g => g.images || []);
  const allReferenceTitles = galleries.flatMap(g => (g.images || []).map(() => g.title));

  return (
    <>
      <Helmet>
        <title>{pageData.meta_title || 'Møldrup VVS A/S'}</title>
        <meta name="description" content={pageData.meta_description || ''} />
        <meta property="og:title" content={pageData.meta_title || 'Møldrup VVS A/S'} />
        <meta property="og:description" content={pageData.meta_description || ''} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/0cd8fe7f-0ba4-47e3-908a-4fc623bd309c.jpg" />
        <html lang="da" />
      </Helmet>

      {/* 1. Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-slate-900 text-white overflow-hidden">
        {/* Background Image with Light Mask */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/2e943a73-c441-4273-88b1-524a17d270b0.jpg"
            alt="Møldrup VVS Servicebil"
            className="w-full h-full object-cover opacity-90"
          />
          {/* Soft gradient overlay to ensure text contrast while keeping the image bright and clear */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-900/40 to-slate-950/50" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-24">
          <span className="text-blue-400 text-xs md:text-sm font-extrabold tracking-[0.3em] uppercase block mb-4 drop-shadow-sm">
            Professionelt VVS-arbejde siden 1987
          </span>
          <h1 className="text-3xl md:text-6xl font-extrabold tracking-wider uppercase leading-tight max-w-4xl mx-auto drop-shadow-md">
            VVS nær Hobro, Viborg og Randers i Midtjylland
          </h1>
          <p className="mt-6 text-sm md:text-lg text-slate-100 max-w-2xl mx-auto leading-relaxed drop-shadow-sm">
            Møldrup VVS A/S leverer kvalitetsbevidst og troværdigt VVS-arbejde til både private, erhverv og offentlige institutioner i hele Danmark.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-4">
            <Link
              to="/ydelser"
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold tracking-widest uppercase px-8 py-4 rounded-none transition-colors duration-200 text-xs flex items-center justify-center space-x-2 shadow-lg"
            >
              <span>Vores Ydelser</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/kontakt"
              className="w-full sm:w-auto border border-white/40 hover:border-white bg-white/10 hover:bg-white/20 text-white font-bold tracking-widest uppercase px-8 py-4 rounded-none transition-all duration-200 text-xs flex items-center justify-center backdrop-blur-sm"
            >
              Kontakt Os
            </Link>
          </div>
        </div>
      </section>

      {/* 2. Services Grid Section ("FRA START TIL SLUT") */}
      <section className="py-24 bg-white text-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Header Column */}
            <div className="lg:col-span-5 space-y-6">
              <span className="text-blue-600 text-xs font-extrabold tracking-[0.25em] uppercase block">
                Fra start til slut
              </span>
              <h2 className="text-2xl md:text-4xl font-extrabold tracking-wider uppercase leading-tight">
                Vi klarer alle VVS-opgaver
              </h2>
              <p className="text-gray-600 leading-relaxed text-sm">
                Hos Møldrup VVS A/S spænder vores kompetencer bredt. Vi udfører alt fra mindre serviceopgaver og reparationer i private hjem til store, komplekse installationer i nybyggeri og erhvervsdomiciler.
              </p>
              <p className="text-gray-600 leading-relaxed text-sm">
                Uanset opgavens størrelse går vi aldrig på kompromis med kvaliteten. Vi rådgiver dig professionelt, så du får den mest energioptimerede og holdbare løsning.
              </p>
              <div className="pt-4">
                <Link
                  to="/ydelser"
                  className="inline-flex items-center space-x-2 text-xs font-bold tracking-widest uppercase text-blue-600 hover:text-blue-800 transition-colors duration-150"
                >
                  <span>Se alle vores kompetencer</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Right Grid Column - Beautiful Bright Cards */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Card 1: Varmepumper */}
              <div className="group bg-slate-50 border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src="https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/87e807fd-91cc-4571-9043-f1cd2be62d68.jpg"
                    alt="Varmepumper & Jordvarme"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 space-y-3">
                  <div className="flex items-center space-x-2 text-blue-600">
                    <Flame className="w-5 h-5" />
                    <h3 className="font-extrabold tracking-wider uppercase text-base text-slate-900">
                      Varme & Energi
                    </h3>
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    Varmepumper, jordvarme, fjernvarme og solvarme. Vi sikrer dig en energioptimeret og grøn varmeløsning.
                  </p>
                </div>
              </div>

              {/* Card 2: Badeværelser */}
              <div className="group bg-slate-50 border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src="https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/b4da8d98-d34d-46de-b1d1-17978c9253fd.jpg"
                    alt="Nyt Badeværelse"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 space-y-3">
                  <div className="flex items-center space-x-2 text-blue-600">
                    <Droplet className="w-5 h-5" />
                    <h3 className="font-extrabold tracking-wider uppercase text-base text-slate-900">
                      Badeværelser
                    </h3>
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    Renovering, sanitet, armaturer og komplette badeværelsesløsninger tilpasset dine ønsker og behov.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Brand History Showcase ("BYGGE PÅ GENERATIONER") - Light & Premium */}
      <section className="grid grid-cols-1 lg:grid-cols-2 bg-slate-50 text-slate-900 border-y border-slate-100">
        {/* Left Column: Image Graphic */}
        <div className="relative min-h-[400px] lg:min-h-[600px] overflow-hidden">
          <img
            src="https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/183e8e54-c0cb-4774-9f08-5620151c8a1a.jpg"
            alt="VVS Installation"
            className="w-full h-full object-cover opacity-90"
          />
          {/* Stylized Watermark Text - Light and elegant */}
          <div className="absolute bottom-12 left-12 select-none pointer-events-none">
            <span className="text-white/60 drop-shadow-md font-black text-8xl md:text-[12rem] leading-none tracking-tighter uppercase block">
              1987
            </span>
          </div>
        </div>

        {/* Right Column: Brand Story - Light & Clean */}
        <div className="bg-white p-12 lg:p-24 flex flex-col justify-center space-y-6">
          <span className="text-blue-600 text-xs font-extrabold tracking-[0.25em] uppercase block">
            Vores Historie
          </span>
          <h2 className="text-2xl md:text-4xl font-extrabold tracking-wider uppercase leading-tight text-slate-900">
            Bygget på generationer og solidt håndværk
          </h2>
          <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
            <p>
              Møldrup VVS A/S blev etableret i 1987 af Minna og Mogens Kastrup Nielsen. Igennem årene er såvel virkefeltet som værksteds- og lagerbygninger udvidet betydeligt, og i 1998 blev firmaet omdannet til et aktieselskab. Arbejdsområdet er i dag det meste af Danmark.
            </p>
            <p>
              I 2013 blev Dennis Würtz partner i Møldrup VVS A/S som led i et generationsskifte. Generations-skiftet afsluttes i 2017, hvor Mogens stopper, og Dennis overtager den sidste del af virksomheden. Vores målsætning er at fremstå som en kvalitetsbevidst og troværdig VVS-virksomhed med et højt serviceniveau, der leverer kvalitetsarbejde til den rette pris og den aftalte tid.
            </p>
          </div>
          <div className="pt-4">
            <Link
              to="/om-os"
              className="inline-flex items-center space-x-2 text-xs font-bold tracking-widest uppercase text-blue-600 hover:text-blue-800 transition-colors duration-150"
            >
              <span>Læs mere om os</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 4. Service Highlights Split Section ("DIT ALSIDIGE...") */}
      <section className="py-24 bg-white text-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Content Area */}
            <div className="lg:col-span-7 space-y-8">
              <span className="text-blue-600 text-xs font-extrabold tracking-[0.25em] uppercase block">
                Professionel Partner
              </span>
              <h2 className="text-2xl md:text-4xl font-extrabold tracking-wider uppercase leading-tight">
                Dit alsidige VVS- og blikkenslagerfirma
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-50 p-2 text-blue-600 rounded-none">
                      <Droplet className="w-5 h-5" />
                    </div>
                    <h3 className="font-extrabold uppercase tracking-wider text-sm text-slate-900">VVS Installation</h3>
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    Vi udfører alt inden for traditionelt VVS-arbejde, herunder vand, varme, sanitet, afløb og serviceaftaler.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-50 p-2 text-blue-600 rounded-none">
                      <Hammer className="w-5 h-5" />
                    </div>
                    <h3 className="font-extrabold uppercase tracking-wider text-sm text-slate-900">Blikkenslager</h3>
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    Professionelt blikkenslagerarbejde i zink og kobber, herunder tagrender, inddækninger og facadearbejde.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-50 p-2 text-blue-600 rounded-none">
                      <Wind className="w-5 h-5" />
                    </div>
                    <h3 className="font-extrabold uppercase tracking-wider text-sm text-slate-900">Ventilation & Genvex</h3>
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    Sikr et sundt og behageligt indeklima med moderne ventilationsanlæg og varmegenvinding.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-50 p-2 text-blue-600 rounded-none">
                      <ShieldCheck className="w-5 h-5" />
                    </div>
                    <h3 className="font-extrabold uppercase tracking-wider text-sm text-slate-900">Garantiordning</h3>
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    Som medlem af Håndværkergarantien er dit byggeri og din økonomi altid sikret mod fejl og mangler.
                  </p>
                </div>
              </div>

              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold tracking-widest uppercase px-8 py-4 rounded-none transition-colors duration-200 text-xs inline-flex items-center space-x-2 shadow-md"
                >
                  <span>Indhent et tilbud</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Right Column: Visual Accent Box - Vibrant Blue Gradient */}
            <div className="lg:col-span-5 bg-gradient-to-br from-blue-600 to-blue-800 p-12 text-center flex flex-col justify-center items-center shadow-xl rounded-none text-white min-h-[400px]">
              <Award className="w-16 h-16 text-white mb-6" />
              <h3 className="font-black tracking-wider uppercase text-2xl md:text-3xl leading-tight max-w-xs">
                VI STÅR FOR KVALITETS HÅNDVÆRK
              </h3>
              <div className="w-12 h-1 bg-white/40 my-6" />
              <p className="text-blue-100 text-xs leading-relaxed max-w-sm">
                Vores målsætning er altid at fremstå som en kvalitetsbevidst og troværdig samarbejdspartner, der leverer til aftalt tid og pris.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Logo Section */}
      <section className="py-12 bg-white border-t border-b border-gray-100 overflow-hidden relative">
        <style dangerouslySetInnerHTML={{__html: `
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee-smooth {
            display: flex;
            width: max-content;
            animation: marquee 55s linear infinite;
          }
          .animate-marquee-smooth:hover {
            animation-play-state: paused;
          }
        `}} />

        <div className="relative w-full overflow-hidden py-4 bg-slate-50/50">
          {/* Gradient Overlays for smooth fade-out on edges */}
          <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none" />
          
          <div className="animate-marquee-smooth gap-16 items-center">
            {/* First set of logos */}
            {[
              { name: 'Duravit', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/46c5123d-dccf-41ef-a33b-7e84cab05dee.png' },
              { name: 'Borma', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/459dbca1-9a6c-48d5-89cf-e367edf33eae.png' },
              { name: 'Oras', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/1c3456e1-a294-4cb1-9829-a1d978370a34.png' },
              { name: 'Grohe', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/1e42a8e1-16e3-427b-989a-2f90201f5cc0.png' },
              { name: 'Hansgrohe', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/3ba703f6-b99c-4768-b7de-0d34dc2dc29c.png' },
              { name: 'Vola', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/2e7ef59b-1764-4785-b067-a1d7389ab02b.png' },
              { name: 'Ifö', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/b1349cdf-ed7c-45e5-bb1c-faca3559872b.png' },
              { name: 'Pressalit', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/e9b363d0-9772-4b4b-9c6d-05d0a9b3d067.png' },
            ].map((partner, idx) => (
              <div key={`p1-${idx}`} className="flex items-center justify-center w-36 h-16 shrink-0">
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="max-w-full max-h-12 object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                />
              </div>
            ))}
            {/* Second set of logos for seamless looping */}
            {[
              { name: 'Duravit', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/46c5123d-dccf-41ef-a33b-7e84cab05dee.png' },
              { name: 'Borma', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/459dbca1-9a6c-48d5-89cf-e367edf33eae.png' },
              { name: 'Oras', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/1c3456e1-a294-4cb1-9829-a1d978370a34.png' },
              { name: 'Grohe', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/1e42a8e1-16e3-427b-989a-2f90201f5cc0.png' },
              { name: 'Hansgrohe', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/3ba703f6-b99c-4768-b7de-0d34dc2dc29c.png' },
              { name: 'Vola', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/2e7ef59b-1764-4785-b067-a1d7389ab02b.png' },
              { name: 'Ifö', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/b1349cdf-ed7c-45e5-bb1c-faca3559872b.png' },
              { name: 'Pressalit', logo: 'https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/e9b363d0-9772-4b4b-9c6d-05d0a9b3d067.png' },
            ].map((partner, idx) => (
              <div key={`p2-${idx}`} className="flex items-center justify-center w-36 h-16 shrink-0">
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="max-w-full max-h-12 object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. References Section */}
      <section className="py-24 bg-white text-slate-900 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-blue-600 text-xs font-extrabold tracking-[0.25em] uppercase block">
              Udført Arbejde
            </span>
            <h2 className="text-2xl md:text-4xl font-extrabold tracking-wider uppercase">
              Udvalgte Referencer
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              Her kan du se et udvalg af de projekter, vi har løst for vores tilfredse kunder i hele landet. Klik på et billede for at se det i stor visning.
            </p>
          </div>

          {/* References Grid - Clean & Bright */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {allReferenceImages.slice(0, 6).map((img, idx) => (
              <div
                key={idx}
                onClick={() => openLightbox(allReferenceImages, idx)}
                className="relative aspect-video group overflow-hidden bg-slate-100 rounded-none cursor-pointer shadow-sm hover:shadow-md transition-all duration-300"
              >
                <img
                  src={img}
                  alt={allReferenceTitles[idx]}
                  className="w-full h-full object-cover opacity-100 transition-transform duration-500 group-hover:scale-105"
                />
                {/* Elegant light overlay on hover instead of heavy dark backdrop */}
                <div className="absolute inset-0 bg-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/referencer"
              className="inline-flex items-center space-x-2 text-xs font-bold tracking-widest uppercase text-blue-600 hover:text-blue-800 transition-colors duration-150"
            >
              <span>Se alle vores referencer og gallerier</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <GalleryLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
